import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import Select, { type StylesConfig } from 'react-select';
import { buildReportSelectStyles } from '@/components/Reports/reportSelectStyles';
import * as XLSX from 'xlsx';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { useInfoWindow } from '@/contexts/InfoWindowContext';
import { useConversationBus } from '@hooks/useConversationBus';
import 'react-datepicker/dist/react-datepicker.css';
import ReportService from '@services/ReportService';
import { ClearIndicatorComponent as ClearIndicator } from '@/components/Reports/ClearIndicatorComponent';
import {
  getTimeZoneOffset,
  getEmptyString,
  getCustomFields,
  getDates,
  getMissedLogs,
  buildSortFn,
  efficiencyGetCustomFieldHeader,
} from '../TimeTrackingReport/TimeTrackingReportHelpers';
import type { TableRow, SortDirection } from '../TimeTrackingReport/TimeTrackingReportHelpers';
import './CapturedTimeReport.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SelectOption {
  value: number;
  label: string;
}

type SortDir = 'asc' | 'desc';

// ─── Custom date input ────────────────────────────────────────────────────────

interface CtrDateInputProps {
  value?: string;
  onClick?: () => void;
  onClear: () => void;
}

const CtrDateInput = React.forwardRef<HTMLInputElement, CtrDateInputProps>(
  ({ value, onClick, onClear }, ref) => (
    <div className="ctr-date-input-wrapper">
      <input
        ref={ref}
        type="text"
        value={value || ''}
        onClick={onClick}
        readOnly
        className="ctr-date-input"
        placeholder="MM/DD/YYYY"
        onChange={() => {}}
      />
      <button
        type="button"
        className="ctr-date-icon-btn"
        onClick={value ? onClear : onClick}
        tabIndex={-1}
      >
        <i className="fa fa-calendar" style={{ fontSize: 12 }} />
      </button>
    </div>
  ),
);
CtrDateInput.displayName = 'CtrDateInput';

// ─── Sort icon ────────────────────────────────────────────────────────────────

const SortIcon: React.FC<{ col: string; sorted: string; dir: SortDir }> = ({
  col,
  sorted,
  dir,
}) => (
  <i
    className={
      sorted === col ? (dir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc') : 'fa fa-sort'
    }
    style={{ display: 'inline-block', width: 20 }}
  />
);

// ─── Component ───────────────────────────────────────────────────────────────

const CapturedTimeReport: React.FC = () =>
{
  const isDark = document.documentElement.classList.contains('dark');
  const selectStyles = buildReportSelectStyles(isDark);

  // Filter options
  const [teamOptions, setTeamOptions] = useState<SelectOption[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<SelectOption[]>([]);
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([]);
  const [dashboardOptions, setDashboardOptions] = useState<SelectOption[]>([]);
  const [cardTypeOptions, setCardTypeOptions] = useState<SelectOption[]>([]);
  const [layerOptions, setLayerOptions] = useState<SelectOption[]>([]);
  const [artistCategoryOptions, setArtistCategoryOptions] = useState<SelectOption[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<SelectOption[]>([]);

  // Selected filters
  const [selectedTeams, setSelectedTeams] = useState<SelectOption[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<SelectOption[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<SelectOption[]>([]);
  const [selectedDashboards, setSelectedDashboards] = useState<SelectOption[]>([]);
  const [selectedCardTypes, setSelectedCardTypes] = useState<SelectOption[]>([]);
  const [selectedLayers, setSelectedLayers] = useState<SelectOption[]>([]);
  const [selectedArtistCategories, setSelectedArtistCategories] = useState<SelectOption[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<SelectOption[]>([]);

  // Dates (YYYY-MM-DD)
  const [fromDate, setFromDate] = useState(moment().startOf('isoWeek').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
  const [fromDateEmpty, setFromDateEmpty] = useState(false);
  const [toDateEmpty, setToDateEmpty] = useState(false);
  const [fromDateLess, setFromDateLess] = useState(false);

  // Toggles
  const [showGroup, setShowGroup] = useState(true);
  const [hidePTO, setHidePTO] = useState(false);
  const [includeDisabledUsers, setIncludeDisabledUsers] = useState(false);
  // Refs so callbacks always see the latest value without stale closure
  const showGroupRef = useRef(true);
  const hidePTORef = useRef(false);

  // Table
  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [dateHeaders, setDateHeaders] = useState<string[]>([]);
  const [customFieldKeys, setCustomFieldKeys] = useState<string[]>([]);
  const [capturedTimeLimit, setCapturedTimeLimit] = useState(0);
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [loading, setLoading] = useState(false);
  const [resultsFounds, setResultsFounds] = useState('');
  const [showDateRangeMsg, setShowDateRangeMsg] = useState(false);
  const dateRangeMsgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Initial data load ────────────────────────────────────────────────────

  const { openInfoWindow } = useInfoWindow();

  useConversationBus('conversation-updated', null, data =>
  {
    const { ProjectId, ProjectName } = data.updates;
    if (ProjectId === undefined && ProjectName === undefined) return;
    setTableData(prev =>
      prev.map(row =>
        row.conversationId === data.conversationId
          ? { ...row, project: ProjectName ?? row.project }
          : row,
      ),
    );
  });

  useEffect(() =>
  {
    document.title = 'Corgee';
  }, []);

  useEffect(() =>
  {
    ReportService.GetAllTeams()
      .then((data: any[]) =>
        setTeamOptions((data || []).map(t => ({ value: t.BasicFieldValueId, label: t.Value }))),
      )
      .catch(() => {});

    ReportService.GetAllAsignedTo({
      Teams: '',
      ArtistCategory: '',
      Departments: '',
      GetAll: false,
    } as any)
      .then((data: any[]) =>
        setEmployeeOptions((data || []).map(e => ({ value: e.PersonID, label: e.Name }))),
      )
      .catch(() => {});

    ReportService.GetAllProject({ GroupByDetailId: 0, GroupByLayerId: 0, GroupByValueField: '' })
      .then((data: any) =>
        setProjectOptions(
          (data?.Projects || []).map((p: any) => ({ value: p.ProjectID, label: p.Name })),
        ),
      )
      .catch(() => {});

    ReportService.GetAllCard()
      .then((data: any[]) =>
        setCardTypeOptions((data || []).map(c => ({ value: c.SideMenuId, label: c.Text }))),
      )
      .catch(() => {});

    ReportService.GetLayers()
      .then((data: any[]) =>
        setLayerOptions((data || []).map(l => ({ value: l.LayerId, label: l.Name }))),
      )
      .catch(() => {});

    ReportService.GetArtistCategories()
      .then((data: any[]) =>
        setArtistCategoryOptions(
          (data || []).map(a => ({
            value: a.BasicFieldValueId ?? a.value,
            label: a.Value ?? a.label,
          })),
        ),
      )
      .catch(() => {});

    ReportService.GetDepartments()
      .then((data: any[]) =>
        setDepartmentOptions(
          (data || []).map(d => ({
            value: d.BasicFieldValueId ?? d.value,
            label: d.Value ?? d.label,
          })),
        ),
      )
      .catch(() => {});
  }, []);

  // ── Cascade loaders ──────────────────────────────────────────────────────

  const loadEmployees = useCallback(
    (teams: number[], categories: number[], depts: number[], inactive: boolean) =>
    {
      ReportService.GetAllAsignedTo({
        Teams: teams.join(','),
        ArtistCategory: categories.join(','),
        Departments: depts.join(','),
        GetAll: inactive,
      } as any)
        .then((data: any[]) =>
          setEmployeeOptions((data || []).map(e => ({ value: e.PersonID, label: e.Name }))),
        )
        .catch(() => {});
    },
    [],
  );

  const loadDashboards = useCallback((projectIds: number[]) =>
  {
    if (!projectIds.length)
    {
      setDashboardOptions([]);
      return;
    }
    ReportService.GetDashboardsByProjectIds(projectIds.join(','))
      .then((data: any[]) =>
        setDashboardOptions((data || []).map(d => ({ value: d.DashboardId, label: d.Name }))),
      )
      .catch(() => {});
  }, []);

  // ── Build request ────────────────────────────────────────────────────────

  const buildRequest = useCallback(
    (currentShowGroup: boolean) => ({
      Teams: selectedTeams.map(t => t.value).join(','),
      AsignedTo: selectedEmployees.map(e => e.value).join(','),
      DateTime: fromDate ? moment(fromDate).format('YYYYMMDD') : '',
      Duedate: toDate ? moment(toDate).format('YYYYMMDD') : '',
      projectId: selectedProjects.map(p => p.value).join(','),
      sideMenuId: selectedCardTypes.map(c => c.value).join(','),
      layersId: selectedLayers.map(l => l.value).join(','),
      dashboardsId: selectedDashboards.map(d => d.value).join(','),
      showGroup: currentShowGroup,
      showAttendanceGroup: false,
      getAll: includeDisabledUsers,
      timeZone: getTimeZoneOffset(),
      comments: '',
      employeeids: '',
      status: '',
      artistCategory: selectedArtistCategories.map(a => a.value).join(','),
      departments: selectedDepartments.map(d => d.value).join(','),
    }),
    [
      selectedTeams,
      selectedEmployees,
      fromDate,
      toDate,
      selectedProjects,
      selectedCardTypes,
      selectedLayers,
      selectedDashboards,
      includeDisabledUsers,
      selectedArtistCategories,
      selectedDepartments,
    ],
  );

  // ── Fill table ───────────────────────────────────────────────────────────

  const fillTableCaptureTime = useCallback(
    async (response: any[], currentShowGroup: boolean, currentHidePTO: boolean) =>
    {
      if (!response?.length)
      {
        setResultsFounds('No results found');
        setTableData([]);
        setDateHeaders([]);
        setCustomFieldKeys([]);
        setCapturedTimeLimit(0);
        return;
      }
      setResultsFounds('');
      const rows: TableRow[] = [];
      const allDateKeys: string[] = [];
      const allCfKeys: string[] = [];

      for (let i = 0; i < response.length; i++)
      {
        const item = response[i];
        const taskName = getEmptyString(String(item.TaskName ?? ''));
        const isPTO = taskName.includes('HOLIDAY') || taskName.includes('PTO-');
        if (!currentShowGroup && currentHidePTO && isPTO) continue;

        const hireDate = currentShowGroup
          ? new Date(await ReportService.GetHireDate(Number(item.PersonId)))
          : new Date(0);

        const cf = getCustomFields(item);
        const dates = getDates(item);

        Object.keys(dates).forEach(k =>
        {
          if (!allDateKeys.includes(k)) allDateKeys.push(k);
        });
        Object.keys(cf).forEach(k =>
        {
          if (!allCfKeys.includes(k)) allCfKeys.push(k);
        });

        rows.push({
          id: i + 1,
          personId: Number(item.PersonId),
          employee: String(item.Name),
          team: getEmptyString(String(item.Team ?? '')),
          department: getEmptyString(String(item.Department ?? '')),
          artistcategory: getEmptyString(String(item.ArtistCategory ?? '')),
          project: getEmptyString(String(item.Project ?? '')),
          taskname: taskName,
          phase: getEmptyString(String(item.Phase ?? '')),
          workingdate: String(item.WorkingDate ?? ''),
          capturedate: String(item.CaptureDate ?? ''),
          capturetime: String(item.CaptureTime ?? ''),
          hours: parseFloat(String(item.Hours)).toFixed(2),
          externalestimated: currentShowGroup
            ? 0
            : !isNaN(Number(item.ExternalEstimated))
              ? parseFloat(String(item.ExternalEstimated)).toFixed(2)
              : 'N/A',
          estimated: currentShowGroup ? 0 : parseFloat(String(item.Estimated)).toFixed(2),
          actual: currentShowGroup
            ? 0
            : item.Actual != null
              ? parseFloat(String(item.Actual)).toFixed(2)
              : 'N/A',
          conversationId: currentShowGroup ? 0 : Number(item.ConversationId),
          cardtype: getEmptyString(String(item.CardType ?? '')),
          dates: currentShowGroup ? dates : {},
          missedLogs: currentShowGroup ? getMissedLogs(item, hireDate) : 0,
          progress: isPTO ? 'N/A' : String(item.Progress ?? ''),
          customFields: cf,
        });
      }

      setDateHeaders(allDateKeys);
      setCustomFieldKeys(allCfKeys);
      setTableData(rows);
      setCapturedTimeLimit(Math.min(rows.length, 100));
    },
    [],
  );

  // ── Validation ───────────────────────────────────────────────────────────

  const validate = useCallback((): boolean =>
  {
    let valid = true;
    if (!fromDate)
    {
      setFromDateEmpty(true);
      valid = false;
    }
    else setFromDateEmpty(false);
    if (!toDate)
    {
      setToDateEmpty(true);
      valid = false;
    }
    else setToDateEmpty(false);
    if (fromDate && toDate && moment(toDate).isBefore(moment(fromDate)))
    {
      setFromDateLess(true);
      return false;
    }
    setFromDateLess(false);
    if (fromDate && moment(fromDate).isBefore(moment().subtract(2, 'years')))
    {
      if (dateRangeMsgTimerRef.current) clearTimeout(dateRangeMsgTimerRef.current);
      setShowDateRangeMsg(true);
      dateRangeMsgTimerRef.current = setTimeout(() => setShowDateRangeMsg(false), 3000);
      return false;
    }
    return valid;
  }, [fromDate, toDate]);

  // ── Apply filters ────────────────────────────────────────────────────────

  const applyFilters = useCallback(
    async (currentShowGroup?: boolean, currentHidePTO?: boolean) =>
    {
      if (!validate()) return;
      const sg = currentShowGroup ?? showGroupRef.current;
      const hpto = currentHidePTO ?? hidePTORef.current;
      setLoading(true);
      setTableData([]);
      setResultsFounds('');
      setCapturedTimeLimit(0);
      try
      {
        const req = buildRequest(sg);
        const resp: any[] = await ReportService.GetCaptureTime(req);
        await fillTableCaptureTime(resp, sg, hpto);
      }
      catch (e)
      {
        console.error('CapturedTimeReport error:', e);
        setResultsFounds('Error loading data');
      }
      setLoading(false);
    },
    [validate, buildRequest, fillTableCaptureTime],
  );

  // ── Sorting ──────────────────────────────────────────────────────────────

  const handleSort = (col: string) =>
  {
    const newDir: SortDir = sortCol === col && sortDir === 'asc' ? 'desc' : 'asc';
    setSortCol(col);
    setSortDir(newDir);
    const sortFn = buildSortFn(col, newDir as SortDirection);
    setTableData(prev => [...prev].sort(sortFn));
  };

  // ── Infinite scroll ──────────────────────────────────────────────────────

  const captureTimeScroll = useCallback(() =>
  {
    const div = document.getElementById('captureTimeTableScroll');
    if (!div) return;
    const { scrollHeight, scrollTop, clientHeight } = div;
    if (scrollHeight - scrollTop - clientHeight <= 10)
    {
      setCapturedTimeLimit(prev =>
        prev < tableData.length ? Math.min(prev + 100, tableData.length) : prev,
      );
    }
  }, [tableData.length]);

  // ── Toggles ──────────────────────────────────────────────────────────────

  const toggleShowGroup = () =>
  {
    const newVal = !showGroupRef.current;
    showGroupRef.current = newVal;
    hidePTORef.current = false;
    setShowGroup(newVal);
    setHidePTO(false);
    setTableData([]);
    setDateHeaders([]);
    setCapturedTimeLimit(0);
    setTimeout(() => applyFilters(newVal, false));
  };

  const toggleHidePTO = () =>
  {
    const newVal = !hidePTORef.current;
    hidePTORef.current = newVal;
    setHidePTO(newVal);
    setTableData([]);
    setCapturedTimeLimit(0);
    setTimeout(() => applyFilters(showGroupRef.current, newVal));
  };

  const toggleIncludeDisabledUsers = () =>
  {
    setIncludeDisabledUsers(prev =>
    {
      const next = !prev;
      loadEmployees(
        selectedTeams.map(t => t.value),
        selectedArtistCategories.map(a => a.value),
        selectedDepartments.map(d => d.value),
        next,
      );
      return next;
    });
  };

  // ── Export ───────────────────────────────────────────────────────────────

  const exportToExcel = () =>
  {
    if (!tableData.length) return;

    const customFieldHeaders = customFieldKeys.map(efficiencyGetCustomFieldHeader);
    const detailHeaders = [
      'Project',
      'Card Name',
      'Card Type',
      'Phase Name',
      'Working Date',
      'Captured Date',
      'Captured Time',
      'Hours',
      'External Estimated',
      'Estimated',
      'Actual',
      'Progress',
      'URL',
    ];
    const headerOrder = [
      'EmployeeId',
      'Employee',
      'Team',
      'Department',
      'Artist Category',
      ...customFieldHeaders,
      ...(showGroup ? ['Missed Logs', ...dateHeaders] : detailHeaders),
    ];

    // Mirror exactly what the on-screen table shows -- same columns, same order
    const rows = tableData.map(row =>
    {
      const exportRow: Record<string, string | number> = {
        EmployeeId: row.personId ?? '',
        Employee: row.employee ?? '',
        Team: row.team ?? '',
        Department: row.department ?? '',
        'Artist Category': row.artistcategory ?? '',
      };

      customFieldKeys.forEach(key =>
      {
        exportRow[efficiencyGetCustomFieldHeader(key)] = (row.customFields ?? {})[key] ?? '';
      });

      if (showGroup)
      {
        exportRow['Missed Logs'] = row.missedLogs ?? '';
        dateHeaders.forEach(header =>
        {
          exportRow[header] = (row.dates ?? {})[header] ?? '';
        });
      }
      else
      {
        exportRow.Project = row.project ?? '';
        exportRow['Card Name'] = row.taskname ?? '';
        exportRow['Card Type'] = row.cardtype ?? '';
        exportRow['Phase Name'] = row.phase ?? '';
        exportRow['Working Date'] = row.workingdate ?? '';
        exportRow['Captured Date'] = row.capturedate ?? '';
        exportRow['Captured Time'] = row.capturetime ?? '';
        exportRow.Hours = row.hours ?? '';
        exportRow['External Estimated'] = row.externalestimated ?? '';
        exportRow.Estimated = row.estimated ?? '';
        exportRow.Actual = row.actual ?? '';
        exportRow.Progress = row.progress ?? '';
        exportRow.URL =
          `${window.location.protocol}//${window.location.hostname}${window.location.pathname}` +
          `Dashboard/ConversationDetails/#/${row.conversationId}`;
      }

      return exportRow;
    });

    const ws = XLSX.utils.json_to_sheet(rows, { header: headerOrder });

    if (!showGroup)
    {
      const urlColIndex = headerOrder.indexOf('URL');
      if (urlColIndex !== -1)
      {
        for (let rowIdx = 1; rowIdx <= rows.length; rowIdx++)
        {
          const cellAddress = XLSX.utils.encode_cell({ r: rowIdx, c: urlColIndex });
          const cellUrl = String(rows[rowIdx - 1]?.URL ?? '');
          ws[cellAddress] = { f: `HYPERLINK("${cellUrl}","${cellUrl}")`, t: 'f' };
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, 'CapturedTime.xlsx');
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="ctr-report" id="CapturedTimeReport">
      <h3 className="ctr-title">Captured Time Report</h3>
      <p className="ctr-subtitle">
        This report contains information on the hours logged by the users in their cards.
        <br></br>
        Dashboard filter will only display information from Default Columns or Dynamic Column, never from both.
      </p>

      <div className="ctr-filters">
        <div className="ctr-filter-grid">
          <div className="ctr-filter-item">
            <label className="ctr-label">Team(s):</label>
            <Select
              isMulti
              options={teamOptions}
              value={selectedTeams}
              onChange={v =>
              {
                const teams = (v as SelectOption[]) || [];
                setSelectedTeams(teams);
                setSelectedEmployees([]);
                loadEmployees(
                  teams.map(t => t.value),
                  selectedArtistCategories.map(a => a.value),
                  selectedDepartments.map(d => d.value),
                  includeDisabledUsers,
                );
              }}
              placeholder="Select..."
              styles={selectStyles as StylesConfig<SelectOption, true>}
              closeMenuOnSelect={false}
              components={{ ClearIndicator }}
            />
          </div>
          <div className="ctr-filter-item">
            <label className="ctr-label">Employee(s):</label>
            <Select
              isMulti
              options={employeeOptions}
              value={selectedEmployees}
              onChange={v => setSelectedEmployees((v as SelectOption[]) || [])}
              placeholder="Select..."
              styles={selectStyles as StylesConfig<SelectOption, true>}
              closeMenuOnSelect={false}
              components={{ ClearIndicator }}
            />
          </div>
          <div className="ctr-filter-item">
            <label className="ctr-label">Card Type(s):</label>
            <Select
              isMulti
              options={cardTypeOptions}
              value={selectedCardTypes}
              onChange={v => setSelectedCardTypes((v as SelectOption[]) || [])}
              placeholder="Select..."
              styles={selectStyles as StylesConfig<SelectOption, true>}
              closeMenuOnSelect={false}
              components={{ ClearIndicator }}
            />
          </div>
          <div className="ctr-filter-item">
            <label className="ctr-label">Project(s):</label>
            <Select
              isMulti
              options={projectOptions}
              value={selectedProjects}
              onChange={v =>
              {
                const projs = (v as SelectOption[]) || [];
                setSelectedProjects(projs);
                setSelectedDashboards([]);
                loadDashboards(projs.map(p => p.value));
              }}
              placeholder="Select..."
              styles={selectStyles as StylesConfig<SelectOption, true>}
              closeMenuOnSelect={false}
              components={{ ClearIndicator }}
            />
          </div>
          <div className="ctr-filter-item">
            <label className="ctr-label">Dashboard(s):</label>
            <Select
              isMulti
              options={dashboardOptions}
              value={selectedDashboards}
              onChange={v => setSelectedDashboards((v as SelectOption[]) || [])}
              placeholder="Select..."
              styles={selectStyles as StylesConfig<SelectOption, true>}
              closeMenuOnSelect={false}
              components={{ ClearIndicator }}
            />
          </div>
          <div className="ctr-filter-item">
            <label className="ctr-label">Layer(s):</label>
            <Select
              isMulti
              options={layerOptions}
              value={selectedLayers}
              onChange={v => setSelectedLayers((v as SelectOption[]) || [])}
              placeholder="Select..."
              styles={selectStyles as StylesConfig<SelectOption, true>}
              closeMenuOnSelect={false}
              components={{ ClearIndicator }}
            />
          </div>
          <div className="ctr-filter-item">
            <label className="ctr-label">Artist Category:</label>
            <Select
              isMulti
              options={artistCategoryOptions}
              value={selectedArtistCategories}
              onChange={v =>
              {
                const cats = (v as SelectOption[]) || [];
                setSelectedArtistCategories(cats);
                setSelectedEmployees([]);
                loadEmployees(
                  selectedTeams.map(t => t.value),
                  cats.map(a => a.value),
                  selectedDepartments.map(d => d.value),
                  includeDisabledUsers,
                );
              }}
              placeholder="Select..."
              styles={selectStyles as StylesConfig<SelectOption, true>}
              closeMenuOnSelect={false}
              components={{ ClearIndicator }}
            />
          </div>
          <div className="ctr-filter-item">
            <label className="ctr-label">Department:</label>
            <Select
              isMulti
              options={departmentOptions}
              value={selectedDepartments}
              onChange={v =>
              {
                const depts = (v as SelectOption[]) || [];
                setSelectedDepartments(depts);
                setSelectedEmployees([]);
                loadEmployees(
                  selectedTeams.map(t => t.value),
                  selectedArtistCategories.map(a => a.value),
                  depts.map(d => d.value),
                  includeDisabledUsers,
                );
              }}
              placeholder="Select..."
              styles={selectStyles as StylesConfig<SelectOption, true>}
              closeMenuOnSelect={false}
              components={{ ClearIndicator }}
            />
          </div>
          <div className="ctr-filter-item ctr-filter-item--date">
            <label className="ctr-label">From:</label>
            <DatePicker
              selected={fromDate ? moment(fromDate, 'YYYY-MM-DD').toDate() : null}
              onChange={(d: any) => setFromDate(d ? moment(d).format('YYYY-MM-DD') : '')}
              maxDate={new Date()}
              dateFormat="MM/dd/yyyy"
              customInput={<CtrDateInput onClear={() => setFromDate('')} />}
              popperProps={{ strategy: 'fixed' }}
              showPopperArrow={false}
              calendarClassName="ctr-datepicker-calendar"
            />
            {fromDateEmpty && <span className="ctr-validation">Date is required</span>}
            {fromDateLess && (
              <span className="ctr-validation">From date must be less than To date</span>
            )}
          </div>
          <div className="ctr-filter-item ctr-filter-item--date">
            <label className="ctr-label">To:</label>
            <DatePicker
              selected={toDate ? moment(toDate, 'YYYY-MM-DD').toDate() : null}
              onChange={(d: any) => setToDate(d ? moment(d).format('YYYY-MM-DD') : '')}
              maxDate={new Date()}
              dateFormat="MM/dd/yyyy"
              customInput={<CtrDateInput onClear={() => setToDate('')} />}
              popperProps={{ strategy: 'fixed' }}
              showPopperArrow={false}
              calendarClassName="ctr-datepicker-calendar"
            />
            {toDateEmpty && <span className="ctr-validation">Date is required</span>}
          </div>

          {/* Actions sit in the same grid, one column each, beside To/From */}
          <div className="ctr-checkbox-label ctr-action-cell">
            <span className="corgee_com_checkbox">
              <input
                id="ctr-show-details-cb"
                type="checkbox"
                checked={!showGroup}
                onChange={toggleShowGroup}
              />
              <label htmlFor="ctr-show-details-cb"></label>
            </span>
            <label htmlFor="ctr-show-details-cb" className="ctr-checkbox-text">
              Show Details list
            </label>
          </div>
          <div className="ctr-checkbox-label ctr-action-cell">
            <span className="corgee_com_checkbox">
              <input
                id="ctr-inactive-users-cb"
                type="checkbox"
                checked={includeDisabledUsers}
                onChange={toggleIncludeDisabledUsers}
              />
              <label htmlFor="ctr-inactive-users-cb"></label>
            </span>
            <label htmlFor="ctr-inactive-users-cb" className="ctr-checkbox-text">
              Include Inactive Users
            </label>
          </div>
          {!showGroup && (
            <div className="ctr-checkbox-label ctr-action-cell">
              <span className="corgee_com_checkbox">
                <input
                  id="ctr-hide-pto-cb"
                  type="checkbox"
                  checked={hidePTO}
                  onChange={toggleHidePTO}
                />
                <label htmlFor="ctr-hide-pto-cb"></label>
              </span>
              <label htmlFor="ctr-hide-pto-cb" className="ctr-checkbox-text">
                Hide PTOs
              </label>
            </div>
          )}
          <div className="ctr-action-cell">
            <button className="ctr-apply-btn" onClick={() => applyFilters()} disabled={loading}>
              {loading ? 'Loading...' : 'Apply Filters'}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="ctr-table-wrapper">
        <div className="ctr-export-row">
          <button
            className="ctr-export-btn"
            onClick={exportToExcel}
            title="Export to Excel"
            disabled={!tableData.length}
          >
            <span className="material-icons style-icon-cloud">cloud_download</span> Export
          </button>
        </div>

        <div className="ctr-table-scroll" id="captureTimeTableScroll" onScroll={captureTimeScroll}>
          <table className="ctr-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('personId')}>
                  EmployeeId <SortIcon col="personId" sorted={sortCol} dir={sortDir} />
                </th>
                <th onClick={() => handleSort('employee')}>
                  Employee <SortIcon col="employee" sorted={sortCol} dir={sortDir} />
                </th>
                <th onClick={() => handleSort('team')}>
                  Team <SortIcon col="team" sorted={sortCol} dir={sortDir} />
                </th>
                <th onClick={() => handleSort('department')}>
                  Department <SortIcon col="department" sorted={sortCol} dir={sortDir} />
                </th>
                <th onClick={() => handleSort('artistcategory')}>
                  Artist Category <SortIcon col="artistcategory" sorted={sortCol} dir={sortDir} />
                </th>
                {customFieldKeys.map(key => (
                  <th key={key} onClick={() => handleSort(key)}>
                    {efficiencyGetCustomFieldHeader(key)}{' '}
                    <SortIcon col={key} sorted={sortCol} dir={sortDir} />
                  </th>
                ))}
                {showGroup && (
                  <th onClick={() => handleSort('missedLogs')}>
                    Missed Logs <SortIcon col="missedLogs" sorted={sortCol} dir={sortDir} />
                  </th>
                )}
                {!showGroup && (
                  <>
                    <th onClick={() => handleSort('project-captured')}>
                      Project <SortIcon col="project-captured" sorted={sortCol} dir={sortDir} />
                    </th>
                    <th onClick={() => handleSort('taskname')}>
                      Card Name <SortIcon col="taskname" sorted={sortCol} dir={sortDir} />
                    </th>
                    <th onClick={() => handleSort('card_type')}>
                      Card Type <SortIcon col="card_type" sorted={sortCol} dir={sortDir} />
                    </th>
                    <th onClick={() => handleSort('phase')}>
                      Phase Name <SortIcon col="phase" sorted={sortCol} dir={sortDir} />
                    </th>
                    <th onClick={() => handleSort('workingdate')}>
                      Working Date <SortIcon col="workingdate" sorted={sortCol} dir={sortDir} />
                    </th>
                    <th onClick={() => handleSort('capturedate')}>
                      Captured Date <SortIcon col="capturedate" sorted={sortCol} dir={sortDir} />
                    </th>
                    <th onClick={() => handleSort('capturetime')}>
                      Captured Time <SortIcon col="capturetime" sorted={sortCol} dir={sortDir} />
                    </th>
                    <th onClick={() => handleSort('hours')}>
                      Hours <SortIcon col="hours" sorted={sortCol} dir={sortDir} />
                    </th>
                    <th onClick={() => handleSort('externalestimated')}>
                      External Estimated{' '}
                      <SortIcon col="externalestimated" sorted={sortCol} dir={sortDir} />
                    </th>
                    <th onClick={() => handleSort('estimated')}>
                      Estimated <SortIcon col="estimated" sorted={sortCol} dir={sortDir} />
                    </th>
                    <th onClick={() => handleSort('actual')}>
                      Actual <SortIcon col="actual" sorted={sortCol} dir={sortDir} />
                    </th>
                    <th onClick={() => handleSort('progress')}>
                      Progress <SortIcon col="progress" sorted={sortCol} dir={sortDir} />
                    </th>
                  </>
                )}
                {showGroup &&
                  dateHeaders.map(h => (
                    <th key={h} onClick={() => handleSort(h)}>
                      {h} <SortIcon col={h} sorted={sortCol} dir={sortDir} />
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {tableData.slice(0, capturedTimeLimit).map((row, i) => (
                <tr key={i}>
                  <td>{row.personId}</td>
                  <td>{row.employee}</td>
                  <td className={row.team === 'N/A' ? 'ctr-na' : ''}>{row.team}</td>
                  <td className={row.department === 'N/A' ? 'ctr-na' : ''}>{row.department}</td>
                  <td className={row.artistcategory === 'N/A' ? 'ctr-na' : ''}>
                    {row.artistcategory}
                  </td>
                  {Object.entries(row.customFields ?? {}).map(([k, v]) => (
                    <td key={k}>{v}</td>
                  ))}
                  {showGroup && (
                    <td className={row.missedLogs === 0 ? 'ctr-na' : ''}>{row.missedLogs}</td>
                  )}
                  {!showGroup && (
                    <>
                      <td>{row.project}</td>
                      <td>
                        <a
                          className="capacity-task-title"
                          onClick={() => row.conversationId && openInfoWindow(row.conversationId)}
                        >
                          {row.taskname}
                        </a>
                      </td>
                      <td className={row.cardtype === 'N/A' ? 'ctr-na' : ''}>{row.cardtype}</td>
                      <td>{row.phase}</td>
                      <td>{row.workingdate}</td>
                      <td>{row.capturedate}</td>
                      <td>{row.capturetime}</td>
                      <td>{row.hours}</td>
                      <td className={String(row.externalestimated) === 'N/A' ? 'ctr-na' : ''}>
                        {row.externalestimated}
                      </td>
                      <td>{row.estimated}</td>
                      <td className={String(row.actual) === 'N/A' ? 'ctr-na' : ''}>{row.actual}</td>
                      <td className={row.progress === 'N/A' ? 'ctr-na' : ''}>{row.progress}</td>
                    </>
                  )}
                  {showGroup &&
                    Object.entries(row.dates ?? {}).map(([k, v]) => (
                      <td key={k} className={v === '0.00' ? 'ctr-na' : ''}>
                        {v}
                      </td>
                    ))}
                </tr>
              ))}
              {!loading && tableData.length === 0 && (
                <tr>
                  <td colSpan={100} className="ctr-no-data">
                    {resultsFounds ||
                      'Please select a date range and apply filters to display results'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {showDateRangeMsg &&
        createPortal(
          <>
            <style>{`
              @keyframes ctr-date-msg {
                0%   { opacity: 0; }
                10%  { opacity: 1; }
                90%  { opacity: 1; }
                100% { opacity: 0; }
              }
            `}</style>
            <div
              style={{
                position: 'fixed',
                top: '64px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#62abcb',
                color: '#fff',
                padding: '8px 20px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                pointerEvents: 'none',
                zIndex: 9999,
                whiteSpace: 'nowrap',
                animation: 'ctr-date-msg 3s ease forwards',
              }}
            >
              The start date must be within the last 2 years
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};

export default CapturedTimeReport;
